/**
 * 表单输入。无障碍要点：
 *  - label 始终与 input 通过 htmlFor/id 关联（不用 placeholder 代替 label）
 *  - 错误用 aria-invalid + aria-describedby 关联，读屏能念出来
 *
 * ## 「不要可见 label」怎么用（`label=""`）
 *
 * `label` **仍然是必填**（每个调用点都必须显式想一下"这一格叫什么"），
 * 但传**空字符串**时 `FieldShell` 不渲染那个 `<label>` 元素 —— 只去掉"看得见的字"，
 * 不动盒子结构（`space-y-1.5` 在只剩一个孩子时不多出任何高度）。
 *
 * ⚠️ 传空串的调用点**必须**自己给 input 一个 `aria-label`
 * （例如日期输入框：`label="" aria-label="选择日期"`），否则读屏会念不出这一格是什么。
 * 这样写而不是"给 label 加 sr-only/隐藏类"的原因：本轮要的是**去掉**那两个字的视觉，
 * 而 `aria-label` 是这条路径上更直接、也更不容易被样式改坏的可访问名字来源。
 */
import { useId, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes } from 'react';
import { cn } from '../../lib/utils';

interface FieldShellProps {
  /** 空串 = 不渲染可见的 `<label>`（调用点负责给控件 `aria-label`）。 */
  label: string;
  htmlFor: string;
  /**
   * 追加到 `<label>` 上的类（**只影响标签这一块**）。
   *
   * ## 为什么需要它（2026-10-18：三格控件对齐）
   *
   * 默认的 `block` 标签在**一行里有几格**的布局中会出问题：某一格的标签
   * （例如「收录到哪张表（新建时）」）换行成两行时，它下面的控件就被推到更低处，
   * 与旁边两格的控件**不在一条线上**。
   *
   * 所以这里把"标签占多高、怎么对齐"交给调用点显式声明（例如
   * `flex min-h-[2.5rem] items-end` = 统一按两行高、文字贴底），
   * 而不是在调用点复制一份 label 结构。
   *
   * ⚠️ 只加在需要它的调用点上：不传 = 原来的 `block` 行为，一个字都不变。
   */
  labelClassName?: string;
  hint?: ReactNode;
  error?: string | null;
  children: ReactNode;
  /** 儿童模式：放大字号与点击区 */
  kid?: boolean;
}

function FieldShell({ label, htmlFor, labelClassName, hint, error, children, kid }: FieldShellProps) {
  const hasVisibleLabel = label.trim() !== '';
  return (
    <div className="space-y-1.5">
      {hasVisibleLabel && (
        <label
          htmlFor={htmlFor}
          className={cn(
            'block font-medium text-slate-700',
            kid ? 'text-lg' : 'text-sm',
            labelClassName,
          )}
        >
          {label}
        </label>
      )}
      {children}
      {hint && !error && (
        <p id={`${htmlFor}-hint`} className="text-xs text-slate-500">
          {hint}
        </p>
      )}
      {error && (
        <p id={`${htmlFor}-error`} role="alert" className="text-xs font-medium text-rose-600">
          {error}
        </p>
      )}
    </div>
  );
}

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  /** 空串 = 不要可见的标签（此时**必须**自带 `aria-label`，见文件头）。 */
  label: string;
  /** 追加到 `<label>` 上的类（见 `FieldShellProps.labelClassName`）。 */
  labelClassName?: string;
  hint?: ReactNode;
  error?: string | null;
  /** 右侧附加内容（如"显示密码"按钮、验证码刷新按钮） */
  suffix?: ReactNode;
  kid?: boolean;
}

export function Input({
  label,
  labelClassName,
  hint,
  error,
  suffix,
  kid = false,
  className,
  id,
  ...rest
}: InputProps) {
  const autoId = useId();
  const inputId = id ?? autoId;
  const describedBy = error ? `${inputId}-error` : hint ? `${inputId}-hint` : undefined;

  return (
    <FieldShell
      label={label}
      htmlFor={inputId}
      {...(labelClassName === undefined ? {} : { labelClassName })}
      hint={hint}
      error={error}
      kid={kid}
    >
      <div className="relative">
        <input
          id={inputId}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          className={cn(
            'w-full rounded-xl border bg-white text-slate-900 placeholder:text-slate-400',
            'focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100',
            kid ? 'min-h-[56px] px-4 text-xl' : 'h-11 px-3 text-base',
            error ? 'border-rose-400' : 'border-slate-300',
            Boolean(suffix) && 'pr-24',
            className,
          )}
          {...rest}
        />
        {suffix && (
          <div className="absolute inset-y-0 right-2 flex items-center gap-1">{suffix}</div>
        )}
      </div>
    </FieldShell>
  );
}

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  /** 空串 = 不要可见的标签（此时**必须**自带 `aria-label`，见文件头）。 */
  label: string;
  /** 追加到 `<label>` 上的类（见 `FieldShellProps.labelClassName`）。 */
  labelClassName?: string;
  hint?: ReactNode;
  error?: string | null;
  kid?: boolean;
}

export function Select({
  label,
  labelClassName,
  hint,
  error,
  kid = false,
  className,
  id,
  ...rest
}: SelectProps) {
  const autoId = useId();
  const selectId = id ?? autoId;
  const describedBy = error ? `${selectId}-error` : hint ? `${selectId}-hint` : undefined;

  return (
    <FieldShell
      label={label}
      htmlFor={selectId}
      {...(labelClassName === undefined ? {} : { labelClassName })}
      hint={hint}
      error={error}
      kid={kid}
    >
      <select
        id={selectId}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        className={cn(
          'w-full appearance-none rounded-xl border bg-white text-slate-900',
          'focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100',
          kid ? 'min-h-[56px] px-4 text-xl' : 'h-11 px-3 text-base',
          error ? 'border-rose-400' : 'border-slate-300',
          className,
        )}
        {...rest}
      />
    </FieldShell>
  );
}
